import { useState, useEffect } from "react";
import Wrapper from "../../components/wrapper/Wrapper";
import SimplePagination from "../../components/Pagination/SimplePagination";
import Loader from "../../components/Loader/Loader";
import toast from "react-hot-toast";
import {
  getAllApportunityRequest,
  getAdminOpportunityRequestConversations,
} from "../../API/ApportunityRequest/ApportunityRequestApi";
import ImagePopUpModal from "../../components/Modal/ImagePopUpModal";
import {
  FiHome,
  FiMapPin,
  FiPhone,
  FiGlobe,
  FiStar,
  FiUser,
  FiMessageSquare,
  FiCalendar,
  FiExternalLink,
  FiX,
  FiCheck,
} from "react-icons/fi";

const ApportunityRequest = () => {
  const [requests, setRequests] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  const fetchApportunityRequests = async (page, limit) => {
    try {
      setLoading(true);
      const response = await getAllApportunityRequest({ page, limit });
      setRequests(response.apportunityRequests || []);
      setPaginationInfo(response.paginationInfo || {});
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error fetching opportunity requests"
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApportunityRequests(page, limit);
  }, [page, limit]);

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleViewConversations = async (request, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      setLoadingConversations(true);
      setSelectedRequest(request);
      const data = await getAdminOpportunityRequestConversations(request._id);
      setConversations(data.conversations || []);
      setIsConversationModalOpen(true);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error fetching conversations"
      );
      console.error(error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedRequest(null);
    setConversations([]);
  };

  const getActionLabel = (action) => {
    const labels = {
      submitted: "Submitted",
      accepted: "Accepted",
      rejected: "Rejected",
      message: "Message",
      work_submitted: "Work Submitted",
    };
    return labels[action] || action;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      in_progress: { color: "bg-blue-100 text-blue-800", text: "In Progress" },
      completed: { color: "bg-green-100 text-green-800", text: "Completed" },
      rejected: { color: "bg-red-100 text-red-800", text: "Rejected" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  return (
    <Wrapper>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Opportunity Requests
            </h1>
            <p className="text-gray-600 mt-1">
              Review business partnership opportunities from athletes
            </p>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {!loading && requests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Athlete
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr
                    key={request._id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleRequestClick(request)}
                  >
                    {/* Business Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {request.businessInfo.photos?.length > 0 ? (
                            <div className="flex gap-1">
                              <ImagePopUpModal
                                imageUrl={request.businessInfo.photos[0]}
                                customClass="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                alt="Business"
                              />
                              {request.businessInfo.photos.length > 1 && (
                                <div className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-xs text-gray-500">
                                  +{request.businessInfo.photos.length - 1}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <FiHome className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {request.businessInfo.business_name}
                          </h3>
                          <p className="text-sm text-gray-500">Business</p>
                        </div>
                      </div>
                    </td>

                    {/* Athlete Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {request.userId?.profilePhoto ? (
                            <img
                              src={request.userId.profilePhoto}
                              alt="Athlete"
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '';
                                e.target.style.display = 'none';
                                const fallback = e.target.nextElementSibling;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-10 h-10 bg-[--primary] rounded-full flex items-center justify-center"
                            style={{ display: request.userId?.profilePhoto ? 'none' : 'flex' }}
                          >
                            <FiUser className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-gray-900">
                            {request.userId
                              ? `${request.userId?.firstName || ""} ${request.userId?.lastName || ""}`.trim() || "N/A"
                              : "N/A"}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">
                            {request.userId?.email || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Message Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        <FiMessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span
                          className="text-sm text-gray-900 cursor-help"
                          title={request.athlete_message}
                        >
                          {request.athlete_message && request.athlete_message.length > 10
                            ? `${request.athlete_message.substring(0, 10)}...`
                            : request.athlete_message || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Status Column */}
                    <td className="px-6 py-4">
                      {getStatusBadge(request.contractStatus || "pending")}
                    </td>

                    {/* Date Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FiCalendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </td>

                    {/* Actions Column */}
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => handleViewConversations(request, e)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <FiMessageSquare className="w-4 h-4" />
                        View Conversations
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <></>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        )}

        {/* Empty State */}
        {!loading && requests.length === 0 && (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FiHome className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No opportunity requests found
            </h3>
            <p className="text-gray-500">
              No business partnership requests are available at the moment.
            </p>
          </div>
        )}
      </div>

      <SimplePagination
        totalPages={paginationInfo.totalPages || 1}
        currentPage={page}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={handleLimitChange}
        totalItems={paginationInfo.totalItems || 0}
      />

      {/* Request Details Modal */}
      {isModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Opportunity Request Details
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Business Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiHome className="w-5 h-5" />
                      Business Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      {/* Business Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Business Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest.businessInfo.business_name}
                        </p>
                      </div>

                      {/* Address */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <FiMapPin className="w-4 h-4" />
                          Address
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest.businessInfo.address}
                        </p>
                      </div>

                      {/* Contact */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <FiPhone className="w-4 h-4" />
                          Contact Number
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest.businessInfo.contact_no}
                        </p>
                      </div>

                      {/* Website */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <FiGlobe className="w-4 h-4" />
                          Website
                        </label>
                        <a
                          href={selectedRequest.businessInfo.website_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[--primary] hover:text-[--primary]/80 transition-colors flex items-center gap-1"
                        >
                          {selectedRequest.businessInfo.website_link}
                          <FiExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      {/* Rating */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <FiStar className="w-4 h-4" />
                          Rating
                        </label>
                        <div className="flex items-center gap-1">
                          <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-sm font-medium text-gray-900">
                            {selectedRequest.businessInfo.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Photos */}
                  {selectedRequest.businessInfo.photos?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Business Photos
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {selectedRequest.businessInfo.photos.map(
                          (photo, idx) => (
                            <ImagePopUpModal
                              key={idx}
                              imageUrl={photo}
                              customClass="w-full h-32 object-cover rounded-lg border border-gray-200"
                              alt="Business photo"
                            />
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Athlete Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiUser className="w-5 h-5" />
                      Athlete Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      {/* Athlete Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest.userId
                            ? `${selectedRequest.userId?.firstName || ""} ${selectedRequest.userId?.lastName || ""}`.trim() || "N/A"
                            : "N/A"}
                        </p>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedRequest.userId?.email || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiMessageSquare className="w-5 h-5" />
                      Athlete Message
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900 leading-relaxed">
                        {selectedRequest.athlete_message}
                      </p>
                    </div>
                  </div>

                  {/* Request Date */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiCalendar className="w-5 h-5" />
                      Request Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Request Date
                        </label>
                        <p className="text-sm text-gray-900">
                          {new Date(
                            selectedRequest.createdAt
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversations Modal */}
      {isConversationModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Conversations
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedRequest.businessInfo.business_name} -{" "}
                    {selectedRequest.userId
                      ? `${selectedRequest.userId?.firstName || ""} ${selectedRequest.userId?.lastName || ""}`.trim() || "N/A"
                      : "N/A"}
                  </p>
                </div>
                <button
                  onClick={handleCloseConversationModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingConversations ? (
                <div className="flex justify-center items-center py-12">
                  <Loader />
                </div>
              ) : conversations.length > 0 ? (
                <div className="space-y-4">
                  <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                    {conversations.map((conversation, index) => (
                      <div
                        key={conversation._id}
                        className="relative flex items-start gap-4 pb-6 last:pb-0"
                      >
                        {/* Timeline Dot */}
                        <div
                          className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            conversation.senderType === "athlete"
                              ? "bg-blue-100"
                              : conversation.action === "accepted"
                              ? "bg-green-100"
                              : conversation.action === "rejected"
                              ? "bg-red-100"
                              : "bg-gray-100"
                          }`}
                        >
                          {conversation.senderType === "athlete" ? (
                            <FiUser
                              className={`w-4 h-4 ${
                                conversation.senderType === "athlete"
                                  ? "text-blue-600"
                                  : "text-gray-600"
                              }`}
                            />
                          ) : conversation.action === "accepted" ? (
                            <FiCheck className="w-4 h-4 text-green-600" />
                          ) : conversation.action === "rejected" ? (
                            <FiX className="w-4 h-4 text-red-600" />
                          ) : (
                            <FiMessageSquare className="w-4 h-4 text-gray-600" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">
                              {conversation.senderType === "athlete"
                                ? "Athlete"
                                : "Business"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {getActionLabel(conversation.action)}
                            </span>
                            {/* Show status badge for accept/reject actions */}
                            {conversation.action === "accepted" && (
                              <span className="text-xs">
                                {getStatusBadge(
                                  conversation.contractStatus || "in_progress"
                                )}
                              </span>
                            )}
                            {conversation.action === "rejected" && (
                              <span className="text-xs">
                                {getStatusBadge("rejected")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            {conversation.message}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(conversation.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FiMessageSquare className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No conversations found
                  </h3>
                  <p className="text-gray-500">
                    There are no conversations for this opportunity request yet.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseConversationModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default ApportunityRequest;
